import React from 'react';
import PropTypes from "prop-types";
import { displayNumbers } from '../../../variables/numbersFunctions';
import { DocumentFooter } from '../../../components/docs/DocumentFooter';
import entete from "../../../assets/img/entete.jpg";
import { frenchDateOnly } from '../../../variables/functions';

const GeneratedBordereauCommissions = (props) => {
    const emissions = props.emissions || [];

    // ── Période affichée : le FILTRE prime s'il est défini ; sinon on la déduit
    //    automatiquement des dates de règlement des commissions du bordereau. ──
    const reglees = emissions.filter((e) => e.dateReglement);
    const sorted = [...reglees].sort((a, b) => new Date(a.dateReglement) - new Date(b.dateReglement));
    const minStr = sorted[0]?.dateReglement;
    const maxStr = sorted[sorted.length - 1]?.dateReglement;
    const sameDay = minStr && maxStr && new Date(minStr).toDateString() === new Date(maxStr).toDateString();
    const periodeCalculee = minStr
        ? (sameDay ? frenchDateOnly(minStr) : `${frenchDateOnly(minStr)} – ${frenchDateOnly(maxStr)}`)
        : "";
    const periodeAuto = (props.periodeDebut && props.periodeFin)
        ? `${frenchDateOnly(props.periodeDebut)} – ${frenchDateOnly(props.periodeFin)}`
        : (props.periode ? props.periode : periodeCalculee);

    // ── Période sous forme « DU … AU … » (une période comporte deux dates) ──
    // PRIORITÉ à la période SÉLECTIONNÉE par l'utilisateur (persistée sur le bordereau ou choisie
    // dans l'aperçu) ; les anciens bordereaux sans période stockée retombent sur le min/max.
    const periodeDuAu = (props.periodeDebut && props.periodeFin)
        ? `PÉRIODE DU ${frenchDateOnly(props.periodeDebut)} AU ${frenchDateOnly(props.periodeFin)}`
        : (minStr
            ? `PÉRIODE DU ${frenchDateOnly(minStr)} AU ${frenchDateOnly(maxStr)}`
            : (periodeAuto ? `PÉRIODE : ${periodeAuto}` : ""));

    // ── Prime nette (le montant du règlement est TTC) : nette = TTC − accessoires − taxes ──
    const primeNetteOf = (c) => (c.montant || 0) - (c.accessoires || 0) - (c.taxes || 0);

    // ── Totaux ──
    const totalPrimeNette = emissions.reduce((s, c) => s + primeNetteOf(c), 0);
    const totalAccessoires = emissions.reduce((s, c) => s + (c.accessoires || 0), 0);
    const totalTaxes = emissions.reduce((s, c) => s + (c.taxes || 0), 0);
    // Prime TTC = montant du règlement (déjà TTC).
    const totalPTTC = emissions.reduce((s, c) => s + (c.montant || 0), 0);
    const totalCommission = emissions.reduce(
        (s, c) => s + (c.commission != null ? c.commission : (primeNetteOf(c) * (c.tauxCommission || 0) / 100)), 0);

    // ── Retenue fiscale du bénéficiaire (commission brute → retenue → nette à payer) ──
    // PRIORITÉ aux valeurs FIGÉES du bordereau (tauxRetenue / montantRetenue / montantCommissionNette).
    // La retenue (défaut 20% appliqué sur les non-payés) est stockée en base ; elle NE DOIT PAS être
    // recalculée à l'affichage — le taux « live » du bénéficiaire peut valoir 0 et fausserait le net.
    // Repli sur le recalcul uniquement pour les anciens bordereaux sans valeurs stockées.
    const tauxRetenue = props.tauxRetenue != null
        ? props.tauxRetenue
        : (emissions.find((e) => e.tauxRetenue != null && e.tauxRetenue > 0)?.tauxRetenue || 0);
    const montantRetenue = props.montantRetenue != null
        ? props.montantRetenue
        : (totalCommission * tauxRetenue / 100);
    const commissionNette = props.montantCommissionNette != null
        ? props.montantCommissionNette
        : (totalCommission - montantRetenue);

    // ── Informations de PAIEMENT (affichées uniquement lorsque le bordereau est payé) ──
    const modePaiement = props.modePaiement;
    const numeroPaiement = props.numeroPaiement;
    const banquePaiement = props.banque;
    const datePaiement = props.datePaiement;
    const estPaye = !!(datePaiement || numeroPaiement || modePaiement);
    const libelleNumeroPaiement = (modePaiement || "").toUpperCase().includes("VIR")
        ? "Référence du virement"
        : ((modePaiement || "").toUpperCase().includes("CHEQUE") || (modePaiement || "").toUpperCase().includes("CHÈQUE"))
            ? "N° de chèque"
            : "Référence du paiement";

    // ── Styles monochromes (impression noir & blanc) ──
    const border = "1px solid #000";
    const th = {
        border, padding: "5px 6px", fontSize: 11, fontWeight: 700, textAlign: "left",
        background: "#e8e8e8", WebkitPrintColorAdjust: "exact", printColorAdjust: "exact",
    };
    const thR = { ...th, textAlign: "right" };
    const td = { border, padding: "4px 6px", fontSize: 11, color: "#000", verticalAlign: "top" };
    const tdR = { ...td, textAlign: "right", whiteSpace: "nowrap" };
    const tdC = { ...td, textAlign: "center" };
    const tot = { ...tdR, fontWeight: 700, background: "#f2f2f2", WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" };

    return (
        <div className="brd-commission" style={{ color: "#000" }}>
            <style>{`
                .brd-commission .brd-page {
                    display: flex;
                    flex-direction: column;
                    width: 100%;
                    max-width: 1100px;
                    margin: 0 auto;
                    min-height: 78vh;
                }
                .brd-commission .brd-spacer { flex: 1 1 auto; }
                @media print {
                    @page { size: A4 landscape; margin: 8mm; }
                    html, body { margin: 0; height: auto; }
                    .brd-commission { color: #000 !important; }
                    /* Hauteur d'une page A4 paysage (210mm) moins les marges (2 x 8mm) */
                    .brd-commission .brd-page { min-height: 192mm; max-width: none; }
                    .brd-commission table { page-break-inside: auto; }
                    .brd-commission tr { page-break-inside: avoid; page-break-after: auto; }
                    .brd-commission thead { display: table-header-group; }
                    .brd-commission .brd-footer { page-break-inside: avoid; }
                }
            `}</style>

            <div className="brd-page">
                {/* En-tête : logo de la compagnie (si disponible) + en-tête du Pool,
                    comme sur les autres documents de l'application. */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                    {props.logoCompagnie ? (
                        <img
                            src={props.logoCompagnie}
                            alt="Logo compagnie"
                            style={{ width: "50%", maxHeight: 120, objectFit: "contain" }}
                        />
                    ) : null}
                    <img
                        src={entete}
                        alt="En-tête"
                        style={{ width: props.logoCompagnie ? "50%" : "100%", maxHeight: 120, objectFit: "contain" }}
                    />
                </div>

                {/* Titre : N° de bordereau + période (le bénéficiaire figure déjà dans le bloc infos) */}
                <div style={{ textAlign: "center", margin: "10px 0 6px" }}>
                    <div style={{ fontSize: 16, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 }}>
                        BORDEREAU DE COMMISSION N° {props.numeroBordereau || "—"}
                    </div>
                    {periodeDuAu ? (
                        <div style={{ fontSize: 13, fontWeight: 600, marginTop: 2 }}>{periodeDuAu}</div>
                    ) : null}
                    <div style={{ borderBottom: "2px solid #000", width: 90, margin: "6px auto 0" }} />
                </div>

                {/* Contenu tabulaire avec marges gauche/droite */}
                <div style={{ padding: "0 18px" }}>
                {/* Bloc d'informations */}
                <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 8, fontSize: 11 }}>
                    <tbody>
                        <tr>
                            <td style={{ padding: "2px 6px", width: "50%" }}>
                                <b>Bénéficiaire :</b> {props.beneficiaireNom || "—"}
                                {props.beneficiaireType ? ` (${props.beneficiaireType})` : ""}
                            </td>
                            <td style={{ padding: "2px 6px", width: "50%", textAlign: "right" }}>
                                <b>N° Bordereau :</b> {props.numeroBordereau || "—"}
                            </td>
                        </tr>
                        <tr>
                            <td style={{ padding: "2px 6px" }}><b>Période :</b> {periodeAuto || "—"}</td>
                            <td style={{ padding: "2px 6px", textAlign: "right" }}>
                                <b>Nombre de commissions :</b> {emissions.length}
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* Détail des commissions */}
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                        <tr>
                            <th style={{ ...th, width: 28, textAlign: "center" }}>N°</th>
                            <th style={th}>Police</th>
                            <th style={{ ...th, textAlign: "center" }}>Av.</th>
                            <th style={th}>Assuré</th>
                            <th style={{ ...th, textAlign: "center" }}>Effet</th>
                            <th style={{ ...th, textAlign: "center" }}>Échéance</th>
                            <th style={{ ...th, textAlign: "center" }}>Règlement</th>
                            <th style={{ ...th, textAlign: "center" }}>Cat.</th>
                            <th style={thR}>Prime nette</th>
                            <th style={thR}>Access.</th>
                            <th style={thR}>Taxes</th>
                            <th style={thR}>P.TTC</th>
                            <th style={thR}>Taux</th>
                            <th style={{ ...thR, width: 110 }}>Commission</th>
                        </tr>
                    </thead>
                    <tbody>
                        {emissions.map((r, i) => (
                            <tr key={r.codeId || i}>
                                <td style={tdC}>{i + 1}</td>
                                <td style={td}>{r.numeroPolice}</td>
                                <td style={tdC}>{r.avenant || "AN"}</td>
                                <td style={td}>{r.nomSouscripteur} {r.prenomSouscripteur || ""}</td>
                                <td style={tdC}>{r.dateEffet ? frenchDateOnly(r.dateEffet) : "-"}</td>
                                <td style={tdC}>{r.dateEcheance ? frenchDateOnly(r.dateEcheance) : "-"}</td>
                                <td style={tdC}>{r.dateReglement ? frenchDateOnly(r.dateReglement) : "-"}</td>
                                <td style={tdC}>{r.categorie || "-"}</td>
                                <td style={tdR}>{displayNumbers(primeNetteOf(r))}</td>
                                <td style={tdR}>{displayNumbers(r.accessoires || 0)}</td>
                                <td style={tdR}>{displayNumbers(r.taxes || 0)}</td>
                                <td style={tdR}>{displayNumbers(r.montant || 0)}</td>
                                <td style={tdR}>{displayNumbers(r.tauxCommission || 0)} %</td>
                                <td style={{ ...tdR, width: 110 }}>
                                    {displayNumbers(r.commission != null ? r.commission : (primeNetteOf(r) * (r.tauxCommission || 0) / 100))}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td style={{ ...tot, textAlign: "left" }} colSpan={8}>TOTAUX ({emissions.length} commission(s))</td>
                            <td style={tot}>{displayNumbers(totalPrimeNette)}</td>
                            <td style={tot}>{displayNumbers(totalAccessoires)}</td>
                            <td style={tot}>{displayNumbers(totalTaxes)}</td>
                            <td style={tot}>{displayNumbers(totalPTTC)}</td>
                            <td style={tot}>—</td>
                            <td style={{ ...tot, width: 110 }}>{displayNumbers(Math.trunc(totalCommission))}</td>
                        </tr>
                    </tfoot>
                </table>

                {/* Récapitulatif : retenue fiscale → net à payer (la commission brute figure déjà
                    dans le TOTAL du grand tableau). La colonne des montants (largeur 110, alignée à
                    droite) s'aligne sur la colonne « Commission » du grand tableau. */}
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                    <table style={{ borderCollapse: "collapse", fontSize: 12 }}>
                        <tbody>
                            <tr>
                                <td style={{ border, padding: "5px 10px", fontWeight: 600 }}>
                                    Retenue fiscale ({displayNumbers(tauxRetenue)} %)
                                </td>
                                <td style={{ border, padding: "5px 10px", textAlign: "right", width: 110, whiteSpace: "nowrap" }}>
                                    − {displayNumbers(Math.trunc(montantRetenue))} F CFA
                                </td>
                            </tr>
                            <tr>
                                <td style={{ border, padding: "6px 10px", fontWeight: 700, background: "#e8e8e8", WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}>
                                    Commission nette à payer
                                </td>
                                <td style={{ border, padding: "6px 10px", textAlign: "right", width: 110, whiteSpace: "nowrap", fontWeight: 700, background: "#e8e8e8", WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}>
                                    {displayNumbers(Math.trunc(commissionNette))} F CFA
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Informations de paiement — affichées uniquement quand le bordereau est payé. */}
                {estPaye && (
                    <div style={{ marginTop: 16, fontSize: 12 }}>
                        <div style={{ fontWeight: 700, textDecoration: "underline", marginBottom: 4 }}>
                            Informations de paiement
                        </div>
                        <div><b>Mode de paiement :</b> {modePaiement || "-"}</div>
                        <div><b>{libelleNumeroPaiement} :</b> {numeroPaiement || "-"}</div>
                        <div><b>Banque :</b> {banquePaiement || "-"}</div>
                        <div><b>Payé le :</b> {datePaiement ? frenchDateOnly(datePaiement) : "-"}</div>
                        {props.commentaireEncaissement ? (
                            <div style={{ whiteSpace: "pre-wrap" }}><b>Observation :</b> {props.commentaireEncaissement}</div>
                        ) : null}
                    </div>
                )}
                </div>{/* /Contenu tabulaire */}

                {/* Espaceur : pousse le pied de page en bas de la page */}
                <div className="brd-spacer" />

                {/* Signatures : Bénéficiaire, Responsable commercial, Comptabilité, DG */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", margin: "24px 18px 12px", fontSize: 12 }}>
                    {["Bénéficiaire", "Responsable commercial", "Comptabilité", "DG"].map((label) => (
                        <div key={label} style={{ textAlign: "center", width: "23%" }}>
                            <div style={{ fontWeight: 700, marginBottom: 46 }}>{label}</div>
                            <div style={{ borderTop: "1px solid #000", paddingTop: 2 }}>(signature)</div>
                        </div>
                    ))}
                </div>

                <div className="brd-footer">
                    <DocumentFooter />
                </div>
            </div>
        </div>
    );
};

GeneratedBordereauCommissions.propTypes = {
    emissions: PropTypes.array,
    agence: PropTypes.object,
    beneficiaireNom: PropTypes.string,
    beneficiaireType: PropTypes.string,
    numeroBordereau: PropTypes.string,
    periode: PropTypes.string,
    periodeDebut: PropTypes.string,
    periodeFin: PropTypes.string,
    tauxRetenue: PropTypes.number,
    montantRetenue: PropTypes.number,
    montantCommissionNette: PropTypes.number,
    logoCompagnie: PropTypes.string,
    modePaiement: PropTypes.string,
    numeroPaiement: PropTypes.string,
    banque: PropTypes.string,
    datePaiement: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default GeneratedBordereauCommissions;
